// The cards: events, techs, regions. State stores IDs only; these tables are
// static, so an effect can be a function without breaking the JSON round
// trip that online play depends on.
//
// Effects are written against a small vocabulary (see effects.js) so a card
// can describe itself in the log the same way it changes the civ. An event
// with `choices` parks the round until someone picks (RESOLVE_EVENT).
//
// A card with `odds` is a ROLL: `odds.chance(civ)` is the number of faces
// on a d10 that fire it (threat 3 = 3 in 10). The tracks are odds, not
// thresholds - Matt, playtest 3: "it all feels very clockwork... maybe
// these things should be more probabilistic."

export const DIE = 10;

import { UNHAPPY_AT } from './constants';
import { governmentMods, lawMods } from './hall';

// Story goal cards quote their goal's text; GOALS is defined further down.
const GOAL_TEXT = (id) => () => GOALS[id]?.text || '';
// The Assembly's restless streets: the `unrest` mod, from the government
// AND the techs and wonders that calm it (the Civil Service, Federation, the
// Forum) - the tech audit of playtest 10 found only the government was read.
const unrestOf = (civ) => (governmentMods(civ).unrest || 0) + (techMods(civ).unrest || 0) + (wonderMods(civ).unrest || 0);

// ------------------------------------------------------------- events
//
// kind: 'weather' | 'people' | 'omen' | 'civic' | 'echo' | 'goal'
// 'echo' cards are the ones the players' own actions add - the consequences
// they discover later. Everything the commonwealth does leaves a card behind.

export const EVENTS = {
  // ---- the base deck: the world as it is before anyone does anything
  rain: { kind: 'weather', name: 'Rain', text: 'Steady rain this season. The fields do well.', effect: { food: 1 } },
  sun: { kind: 'weather', name: 'Long Summer', text: 'A long, warm summer.', effect: { happiness: 1 } },
  drought: {
    kind: 'weather', name: 'Drought', text: 'The river runs low and the crops fail. The scholars take notes.', effect: { food: -2 },
    removedBy: 'irrigation', reveals: ['fire', 'moisture'],
  },
  drought2: {
    kind: 'weather', name: 'Drought', text: 'The river runs low and the crops fail.', effect: { food: -2 },
    removedBy: 'irrigation',
  },
  cold: { kind: 'weather', name: 'Cold Snap', text: 'A hard frost. Food spoils and tempers fray.', effect: { food: -1, happiness: -1 }, reveals: ['water', 'heat'] },
  raiders: {
    kind: 'people', name: 'Raiders', text: 'Raiders make camp on the shore. Every night they are left alone, they send more.',
    spawn: { kind: 'camp', strength: 2 },
  },
  raiders2: {
    kind: 'people', name: 'Raiders', text: 'Raiders make camp on the shore. Every night they are left alone, they send more.',
    spawn: { kind: 'camp', strength: 2 },
  },
  // ---- STORYLINES. A choice can `adds` the next chapter to the deck (sight
  // unseen) or `removes` one. Matt: "some events should be decisions the
  // group has to make, and based on the choices further events can be added
  // into the deck, so you end up with storylines."

  // The Strangers: wanderers → a tale → a spring, or a grudge.
  wanderers: {
    kind: 'people', name: 'Wanderers', text: 'A family of strangers at the gate, with a cart. They ask to stay.',
    choices: [
      { id: 'in', label: 'Take them in', effect: { population: 1, food: -3 }, adds: 'strangers_tale', stays: true },
      { id: 'coin', label: 'Trade, then send them on', effect: { coin: 4, goods: 3 } },
      { id: 'away', label: 'Turn them away', effect: {}, adds: 'word_spreads', grieve: 'grudge' },
    ],
  },
  strangers_tale: {
    kind: 'echo', name: 'The Strangers\' Tale', text: 'The newcomers say there is a spring in the hills, a day\'s walk out, with water all year.',
    choices: [
      { id: 'follow', label: 'Send someone to find it', effect: { food: -1 }, adds: 'hidden_spring' },
      { id: 'ignore', label: 'Ignore it', effect: { happiness: 1 } },
    ],
  },
  hidden_spring: { kind: 'echo', name: 'The Hidden Spring', text: 'The spring is found. The fields nearest it will not go dry, and the whole valley could be irrigated from it.', effect: { food: 3, happiness: 1 }, adds: 'goal_garden' },
  word_spreads: { kind: 'echo', name: 'Word Spreads', text: 'Travellers avoid the town now. Fewer come to market.', effect: { coin: -1, happiness: -1 } },
  eclipse: { kind: 'omen', name: 'Eclipse', text: 'An eclipse at noon. The whole town stops to watch, and the scholars take notes.', effect: { knowledge: 1, happiness: 1 }, compass: { x: 1 }, reveals: ['wind', 'heat'] },
  sickness: {
    kind: 'omen', name: 'Sickness', text: 'A fever in the low houses. Roll a die: the unhappier the town, the likelier it takes hold.',
    odds: {
      chance: (civ) => feverChance(civ, UNHAPPY_AT + 4 - civ.happiness), against: 'the misery',
      then: { population: -1 },
      thenText: 'It spreads. One person dies.',
      elseText: 'The town is well fed and it passes.',
    },
  },
  harvest: { kind: 'weather', name: 'Good Harvest', text: 'A good harvest. The barns are full.', effect: { food: 2 } },
  // ---- more of the base pool: weather, omens, people, and decisions
  mild_winter: { kind: 'weather', name: 'Mild Winter', text: 'A mild winter: little snow, little hardship.', effect: { food: 1, happiness: 1 } },
  late_frost: { kind: 'weather', name: 'Late Frost', text: 'A frost after planting.', effect: { food: -1 } },
  fishers_luck: { kind: 'weather', name: 'The Fishers\' Luck', text: 'The boats come back full.', effect: { food: 2 }, unlocks: 'seafaring' },
  locusts: { kind: 'weather', name: 'Locusts', text: 'Locusts strip the fields.', effect: { food: -2, happiness: -1 } },
  census: { kind: 'civic', name: 'The Census', text: 'The town is counted and the count written down.', effect: { knowledge: 1 } },
  bandits: {
    kind: 'people', name: 'Bandits on the Road', text: 'Two carts robbed on the road to the ridge. Bandits have a den out there.',
    spawn: { kind: 'den', strength: 1 },
  },
  fire: {
    kind: 'omen', name: 'Fire in the Granary', text: 'A fire in the granary before dawn. Half the store is lost. Rebuild it in stone?',
    choices: [
      { id: 'stone', label: 'Rebuild in stone (2 goods)', requires: { goods: 2 }, effect: { goods: -2, food: -1 }, adds: 'stone_granary' },
      { id: 'wood', label: 'Patch it up', effect: { food: -2 } },
    ],
  },
  stone_granary: { kind: 'echo', name: 'The Stone Granary', text: 'A stone granary keeps out rats and fire.', effect: { food: 2 } },

  // The Teacher: a scholar offers to stay → lessons → the pupils.
  teacher: {
    kind: 'people', name: 'The Travelling Teacher', text: 'A travelling scholar asks for a roof for the winter.',
    choices: [
      { id: 'house', label: 'Give them a house (1 goods)', requires: { goods: 1 }, effect: { goods: -1, knowledge: 1 }, adds: 'lessons' },
      { id: 'season', label: 'Pay for a season (2 coin)', requires: { coin: 2 }, effect: { coin: -2, knowledge: 2 } },
      { id: 'on', label: 'Send them on', effect: {} },
    ],
  },
  lessons: {
    kind: 'echo', name: 'Lessons', text: 'The children can read now. The teacher asks whether to keep the school open after they leave.',
    choices: [
      { id: 'keep', label: 'Keep the school (1 coin)', requires: { coin: 1 }, effect: { coin: -1, knowledge: 2 }, adds: 'the_pupils' },
      { id: 'close', label: 'Close the school', effect: { happiness: 1 } },
    ],
  },
  the_pupils: { kind: 'echo', name: 'The Pupils', text: 'The first pupils are teaching the next.', effect: { knowledge: 3 } },

  // The Wolves: a hard winter → hunt them, guard, or let them be.
  wolves: {
    kind: 'people', name: 'Wolf Winter', text: 'Wolf tracks around the flocks every morning.',
    choices: [
      { id: 'hunt', label: 'Hunt them (1 food)', requires: { food: 1 }, effect: { food: -1 }, adds: 'wolf_pelts' },
      { id: 'guard', label: 'Pay a watch (1 coin)', requires: { coin: 1 }, effect: { coin: -1 } },
      { id: 'leave', label: 'Let them be', effect: {}, adds: 'wolves_bolder' },
    ],
  },
  wolf_pelts: { kind: 'echo', name: 'Wolf Pelts', text: 'The hunt brings back pelts.', effect: { goods: 2, happiness: 1 }, unlocks: 'husbandry' },
  wolves_bolder: {
    kind: 'echo', name: 'The Wolves Grow Bold', text: 'The wolves have a den near the flocks now.',
    spawn: { kind: 'wolves', strength: 2 },
  },

  // The Dispute: two families and a field → a council → a written law.
  dispute: {
    kind: 'civic', name: 'The Dispute', text: 'Two families claim the same field, and the town is taking sides.',
    choices: [
      { id: 'council', label: 'Call a council (1 coin)', requires: { coin: 1 }, effect: { coin: -1, happiness: 1 }, adds: 'council_again' },
      { id: 'split', label: 'Split the field', effect: { food: 2, happiness: 1 } },
      { id: 'strong', label: 'Give it to the bigger family', effect: { happiness: -1, goods: 1 } },
    ],
  },
  council_again: {
    kind: 'echo', name: 'The Council Meets Again', text: 'The council settled it, and now people bring every dispute to it. Should the rulings be written down?',
    choices: [
      { id: 'write', label: 'Write them down', effect: { knowledge: 1, happiness: 1 }, adds: 'first_law', compass: { x: 1, y: 1 } },
      { id: 'talk', label: 'Keep it informal', effect: { happiness: 2, coin: 1 } },
    ],
  },
  first_law: { kind: 'echo', name: 'The First Law', text: 'The first law is carved by the well where everyone can read it. Disputes end sooner.', effect: { happiness: 2, knowledge: 1 }, unlocks: 'statecraft', adds: 'goal_republic' },

  // The Comet: a sign, but of what? The group decides what it means.
  comet: {
    kind: 'omen', name: 'The Comet', text: 'A comet in the evening sky for a week. The town wants to know what it means.',
    choices: [
      { id: 'glory', label: 'A sign of glory', effect: { happiness: 1, knowledge: 1 }, adds: 'bright_year', unlocks: 'arts', compass: { x: 1 } },
      { id: 'doom', label: 'A sign of doom', effect: { happiness: -1, knowledge: 3 }, adds: 'dark_prophecy', compass: { x: -1 } },
    ],
  },
  bright_year: { kind: 'echo', name: 'The Bright Year', text: 'Whatever goes right this year is credited to the comet, and people work harder for it. The scholars want a tower to watch the sky from.', effect: { happiness: 1, coin: 1, food: 1 }, adds: 'goal_observatory' },
  dark_prophecy: {
    kind: 'echo', name: 'The Dark Prophecy', text: 'Whatever goes wrong this year is blamed on the comet. Roll a die: the unhappier the town, the more they believe it.',
    odds: {
      chance: (civ) => feverChance(civ, UNHAPPY_AT + 5 - civ.happiness), against: 'the misery',
      then: { happiness: -2 },
      thenText: 'The town shuts its doors early.',
      elseText: 'It passes.',
    },
  },

  // Travelling Players: the cheapest happiness in the pool, if you pay.
  players: {
    kind: 'people', name: 'Travelling Players', text: 'Travelling players offer a show in exchange for a meal.',
    choices: [
      { id: 'pay', label: 'Feed them (1 food)', requires: { food: 1 }, effect: { food: -1, happiness: 2 }, adds: 'play_remembered', unlocks: 'arts' },
      { id: 'out', label: 'Send them on', effect: {} },
    ],
  },
  play_remembered: { kind: 'echo', name: 'The Play Remembered', text: 'Children act the play out in the lanes.', effect: { happiness: 1 } },

  // ---- STORY GOALS: a chapter that offers the era a purpose of its own.
  // Matt: "adding goals mid-game that are story-driven is essential."
  goal_garden: {
    kind: 'goal', name: 'A Garden of the Valley',
    text: 'With the spring found, the whole valley could be irrigated. It would take a season of everything the town has.',
    choices: [
      { id: 'take', label: 'Make it our great work', text: GOAL_TEXT('garden'), goal: 'garden' },
      { id: 'leave', label: 'Not now', effect: { happiness: 1 } },
    ],
  },
  goal_republic: {
    kind: 'goal', name: 'The Republic',
    text: 'If one law can be written, so can the rest. The council asks to make it the next great work.',
    choices: [
      { id: 'take', label: 'Make it our great work', text: GOAL_TEXT('forum'), goal: 'forum' },
      { id: 'leave', label: 'Not now', effect: { knowledge: 1 } },
    ],
  },
  goal_one_people: {
    kind: 'goal', name: 'One People',
    text: 'The kin across the ridge would move here if there were room. Two peoples, one town.',
    choices: [
      { id: 'take', label: 'Make it our great work', text: GOAL_TEXT('great_hall'), goal: 'great_hall' },
      { id: 'leave', label: 'Not now', effect: { food: 1 } },
    ],
  },
  goal_observatory: {
    kind: 'goal', name: 'The Observatory',
    text: 'The scholars want a tower, and the sky mapped from it.',
    choices: [
      { id: 'take', label: 'Make it our great work', text: GOAL_TEXT('observatory'), goal: 'observatory' },
      { id: 'leave', label: 'Not now', effect: { knowledge: 1 } },
    ],
  },
  goal_old_knowledge: {
    kind: 'goal', name: 'What the Old Ones Knew',
    text: 'The chamber was only the entrance. The scholars think the people before us knew things that could be learned again.',
    choices: [
      { id: 'take', label: 'Make it our great work', text: GOAL_TEXT('archive'), goal: 'archive' },
      { id: 'leave', label: 'Not now', effect: { happiness: 1 } },
    ],
  },

  // The Petition: a base-deck decision, so every era has at least one.
  petition: {
    kind: 'civic', name: 'The Petition', text: 'A crowd in the square with a list: a holiday, or a wall. They want an answer today.',
    choices: [
      { id: 'holiday', label: 'Declare a holiday', effect: { happiness: 2, food: -1 }, adds: 'holiday_remembered' },
      { id: 'wall', label: 'Raise the wall (2 goods): the strongest camp loses 2', requires: { goods: 2 }, effect: { goods: -2, threat: -2 }, adds: 'behind_walls' },
      { id: 'refuse', label: 'Send them home', effect: { happiness: -1 }, grieve: 'grudge', compass: { y: -1 } },
    ],
  },
  holiday_remembered: { kind: 'echo', name: 'Holiday Remembered', text: 'People still talk about the holiday.', effect: { happiness: 1 } },
  behind_walls: { kind: 'echo', name: 'Behind Walls', text: 'The wall holds. The strongest camp loses a point of strength.', effect: { threat: -1 } },
  restless: {
    kind: 'civic', name: 'Restless Streets', text: 'A crowded town grumbles. Roll a die: the more people past four, the likelier it boils over.',
    odds: {
      chance: (civ) => Math.max(0, (civ.population - 4) * 2 + unrestOf(civ)), against: 'the crowding',
      then: { happiness: -1 },
      thenText: 'Too many people, too little room.',
      elseText: 'Small enough to stay calm.',
    },
  },

  // ---- the goal: the vision card offers two goals; the group picks one
  monument: {
    kind: 'goal', name: 'The Vision',
    text: 'The town can raise a great work. Two are offered; choose one together. Every role can put a day into it, and when it stands it gives the town something for good.',
    reveal: 'goal',
  },
  // THE RACE (DESIGN.md "THE ARC"): put on top of the deck the night the
  // race opens. The group chooses one victory to chase.
  the_race: {
    kind: 'goal', name: 'The Race',
    text: 'The other peoples are reaching for the same victories. Choose together which one to chase.',
    reveal: 'victory',
  },
  next_vision: {
    kind: 'goal', name: 'The Next Vision',
    text: 'One great work stands. Choose the next, together.',
    reveal: 'goal',
  },

  // ---- THE RIVALS. Other peoples on the island; see engine/rivals.js. The
  // card knows which people it is about from `civ.rivalMeeting`.
  rival_envoys: {
    kind: 'people', name: 'Envoys from a Neighbouring People',
    text: 'Envoys from the people whose fires you saw. How you receive them decides how they treat you.',
    rival: true,
    choices: [
      { id: 'gift', label: 'A gift of goods (2 goods)', requires: { goods: 2 }, effect: { goods: -2 }, stance: 'friendly' },
      { id: 'coin', label: 'A gift of coin (3 coin)', requires: { coin: 3 }, effect: { coin: -3 }, stance: 'friendly' },
      { id: 'refuse', label: 'Send them home', effect: {}, stance: 'hostile' },
    ],
  },
  rival_demands: {
    kind: 'people', name: 'Demands',
    text: 'Riders at the gate demand tribute. The people across the way think you are weak.',
    rival: true,
    choices: [
      { id: 'pay', label: 'Pay the tribute (4 coin)', requires: { coin: 4 }, effect: { coin: -4 }, stance: 'wary' },
      { id: 'refuse', label: 'Refuse them', effect: { happiness: 1 }, warparty: true },
    ],
  },
  rival_offer: {
    kind: 'people', name: 'A Trading Party',
    text: 'A friendly people sends a trading party with carts.',
    rival: true,
    choices: [
      { id: 'trade', label: 'Buy from them (3 coin)', requires: { coin: 3 }, effect: { coin: -3, goods: 3 } },
      { id: 'sell', label: 'Sell to them (2 goods)', requires: { goods: 2 }, effect: { goods: -2, coin: 4 } },
      { id: 'decline', label: 'Not today', effect: { happiness: 1 } },
    ],
  },

  // ---- echoes: added by the players' own actions
  festival_remembered: { kind: 'echo', name: 'Festival Remembered', text: 'People still talk about the festival.', effect: { happiness: 1 }, compass: { y: 1 } },
  caravan_returns: { kind: 'echo', name: 'Caravan Returns', text: 'A caravan you sent out comes home loaded.', effect: { goods: 1, coin: 1 } },
  eureka: { kind: 'echo', name: 'Eureka', text: 'One idea leads to another.', effect: { knowledge: 2 } },
  // A unit that does not come home leaves this in the deck (Brian and Matt,
  // playtest 9: "we will remember you - that would be a fun thing to put a
  // card in the deck for, for our units being destroyed").
  fallen: {
    kind: 'echo', name: 'We Remember', text: 'A band went out and did not come back. The town gathers to remember them.',
    choices: [
      { id: 'cairn', label: 'Raise a cairn (2 goods): +2 happiness', requires: { goods: 2 }, effect: { goods: -2, happiness: 2 } },
      { id: 'song', label: 'Sing their names: +1 happiness', effect: { happiness: 1 } },
    ],
  },
  anniversary: { kind: 'echo', name: 'Anniversary', text: 'A year since the great work\'s first stone. The town celebrates.', effect: { happiness: 2 } },

  // ---- echoes from the map (see REGIONS)
  valley_harvest: { kind: 'echo', name: 'Valley Harvest', text: 'The valley you found gives another harvest.', effect: { food: 2 } },
  ore_find: { kind: 'echo', name: 'Ore Find', text: 'More copper in the hills.', effect: { goods: 2 } },
  // The Old Road: merchants → a fair, or a toll.
  merchants: {
    kind: 'echo', name: 'Merchants on the Old Road', text: 'Traders are using the road you opened. They will come back if it pays.',
    choices: [
      { id: 'coin', label: 'Sell to them', effect: { coin: 3 }, adds: 'return_custom' },
      { id: 'goods', label: 'Buy from them', effect: { goods: 2 }, adds: 'foreign_fashions' },
      { id: 'toll', label: 'Charge a toll', effect: { coin: 2, happiness: -1 } },
    ],
  },
  return_custom: { kind: 'echo', name: 'Return Custom', text: 'The merchants are back, with more traders.', effect: { coin: 2, goods: 1 } },
  foreign_fashions: { kind: 'echo', name: 'Foreign Fashions', text: 'Everyone wants what the merchants sold.', effect: { happiness: 2 } },

  // The Neighbours: envoys → an alliance or a cold shoulder → kin or a raid.
  envoys: {
    kind: 'echo', name: 'Envoys from the Neighbours', text: 'The people you met across the ridge send envoys. They expect a gift.',
    choices: [
      { id: 'gift', label: 'Send a gift (2 goods)', requires: { goods: 2 }, effect: { goods: -2, happiness: 1, threat: -1 }, adds: 'alliance' },
      // Coin as well as goods (playtest 14: "we don't have anything to offer
      // them" - the friendly answer was out of reach, so it was no choice).
      { id: 'coin', label: 'Send a gift (3 coin)', requires: { coin: 3 }, effect: { coin: -3, happiness: 1, threat: -1 }, adds: 'alliance' },
      { id: 'refuse', label: 'Send them home', effect: { threat: 1 }, adds: 'cold_shoulder' },
    ],
  },
  alliance: {
    kind: 'echo', name: 'An Offer from the Neighbours', text: 'Their elders propose a bond: a marriage, or a trade treaty.',
    choices: [
      { id: 'marry', label: 'A marriage', effect: { population: 1, happiness: 1 }, adds: 'kin_across_the_ridge' },
      { id: 'treaty', label: 'A trade treaty', effect: { coin: 6, goods: 6 }, adds: 'return_custom' },
    ],
  },
  kin_across_the_ridge: { kind: 'echo', name: 'Kin Across the Ridge', text: 'Family now: their harvest helps yours and their walls shelter you. Some of them want to move here.', effect: { food: 2, threat: -1 }, adds: 'goal_one_people' },
  cold_shoulder: {
    kind: 'echo', name: 'Cold Shoulder', text: 'The neighbours remember being turned away. Riders make camp on the border.',
    spawn: { kind: 'camp', strength: 3 },
  },

  // The Ruins: whispers → dig or fill → what was buried.
  omen_of_ruins: {
    kind: 'echo', name: 'Whispers from the Ruins', text: 'There is more under the ruins. The scholars want to keep digging.',
    choices: [
      { id: 'dig', label: 'Dig deeper', effect: { knowledge: 2, happiness: -1 }, adds: 'buried_thing' },
      { id: 'fill', label: 'Fill it in', effect: { happiness: 1 } },
    ],
  },
  buried_thing: {
    kind: 'echo', name: 'The Buried Thing', text: 'A sealed chamber, carved with the same marks as the stones.',
    choices: [
      { id: 'open', label: 'Open it', effect: { knowledge: 3 }, adds: ['something_stirs', 'goal_old_knowledge'], unlocks: 'old_knowledge' },
      { id: 'seal', label: 'Seal it', effect: { happiness: 1, knowledge: 1 } },
    ],
  },
  something_stirs: {
    kind: 'echo', name: 'Something Stirs', text: 'Since the chamber was opened, the low houses sicken. Roll a die: the unhappier the town, the likelier it takes hold.',
    odds: {
      chance: (civ) => feverChance(civ, UNHAPPY_AT + 5 - civ.happiness), against: 'the misery',
      then: { population: -1, happiness: -1 },
      thenText: 'It takes one person.',
      elseText: 'It passes.',
    },
  },
  marsh_fever: {
    kind: 'echo', name: 'Marsh Fever', text: 'The explorers brought a fever back. Roll a die: it takes hold where people are unhappy.',
    odds: {
      chance: (civ) => feverChance(civ, 8 - civ.happiness), against: 'the misery',
      then: { population: -1 },
      thenText: 'The fever takes one person.',
      elseText: 'It passes.',
    },
  },
};

// The base POOL, and how many of it an era draws. The deck is the clock,
// so more content must not mean a longer era: each era deals
// BASE_DRAW.decisions decision cards and BASE_DRAW.others of the rest, so
// no two eras open the same way and every era has stories in it.
export const BASE_POOL = [
  'rain', 'sun', 'drought', 'drought2', 'cold', 'raiders', 'raiders2', 'eclipse', 'sickness', 'harvest', 'restless',
  'mild_winter', 'late_frost', 'fishers_luck', 'locusts', 'census', 'bandits',
  'wanderers', 'petition', 'fire', 'teacher', 'wolves', 'dispute', 'comet', 'players',
];
export const BASE_DRAW = { decisions: 4, others: 8 };
export const isDecision = (id) => Boolean(EVENTS[id]?.choices);
// Kept for anything that still wants "the base deck" as one list.
export const BASE_DECK = BASE_POOL;

export const GOAL_CARD = 'monument';

// ---------------------------------------------------------------- techs
//
// Science pays knowledge; the upgrade lands on somebody ELSE's board. That
// is the whole point of the role - science is not a resource, it's the
// ability to change what everyone else can do.
//
// A TREE, not a list (Matt, 2026-09-14 late: "eventually we just had all
// the technologies... we need a fairly extensive tech tree"). Four tiers;
// a tech needs every tech in `needs` first; cost is 2 + tier knowledge.
// Effects are `mods`, summed over known techs and read by the boards and
// the night (see techMods / modsOf in roleActions.js), plus `removes`.

// A technology costs 2 x tier + 1 knowledge to write down: 3, 5, 7, 9, 11.
// Steep on purpose - the tree is the long game's ladder, and at 2 + tier
// the bots learned the whole of it in forty rounds.
// `forRole` is who it helps: a role id, 'any', or a list of role ids.
const T = (tier, name, forRole, text, mods = {}, extra = {}) => ({
  tier, name, forRole, text, mods, cost: 1 + tier * 2, needs: [], ...extra,
});

export const TECHS = {
  // ---- tier 1: the first ideas
  irrigation: T(1, 'Irrigation', ['military', 'trade'], 'Frontier: +1 on every die a unit rolls working the ground, and on Hunt near the walls. Stewards: a staffed field grows +1 food. Removes one Drought from the deck.', { huntBase: 1 }, { cost: 5, removes: ['drought'] }),
  tracking: T(1, 'Tracking', ['military', 'trade'], 'Frontier: units roll +1 die working the ground, and warbands may grow 1 stronger. Stewards: can train scouts.', { huntDice: 1, bandMax: 1 }, { cost: 5 }),
  currency: T(1, 'Currency', 'trade', 'Markets pay +1 coin.', { marketCoin: 1 }, { cost: 5 }),
  pottery: T(1, 'Pottery', 'trade', 'Buy grain brings +1 food; the Stewards can build a Warehouse.', { grainFood: 1 }, { cost: 5 }),
  writing: T(1, 'Writing', ['science', 'culture'], 'Scholars: Study yields +1 knowledge. Hall: the Law of Weights and the Law of the Well come on offer, and the Scholar may sit on the council.', { study: 1 }, { cost: 5 }),
  rituals: T(1, 'Rituals', 'culture', 'Every rite and song the Hall plays gives +1 happiness.', { hallJoy: 1 }, { cost: 4 }),
  // Roads exist early and the tree upgrades them (Matt, 2026-09-25).
  // Defence is a technology (2026-09-28). `late` so old seeds keep their reactions.
  watch: T(1, 'The Watch', 'military', 'The city throws a die at raiders who reach it; a hit turns them back.', { garrisonDice: 1 }, { cost: 5, late: true }),
  paths: T(1, 'Paths', 'military', 'Units may lay road (1 action, 1 goods): 2 hexes an action along it; road-joined villages send +1.', { roadLevel: 1 }, { cost: 5, late: true }),

  // ---- tier 2: building on them
  masonry: T(2, 'Masonry', 'any', 'The great work costs 1 less goods per stage, and every town throws one more die at raiders.', { buildGoods: -1, townDice: 1 }, { needs: ['pottery'] }),
  // Not a dead end any more (playtest 14): towns too, and Masonry's stone walls stand on it.
  palisades: T(2, 'Palisades', 'military', 'Every village and town throws a die at raiders who reach it; Steel stands on it.', { villageDice: 1, townDice: 1 }, { cost: 6, needs: ['watch'], late: true }),
  bronze: T(2, 'Bronze', 'military', 'Units, and the city\'s, towns\' and villages\' defence dice, hit on 3+ instead of 4+.', { hitOn: -1 }, { cost: 9, needs: ['tracking'] }),
  wheel: T(2, 'The Wheel', 'trade', 'Caravans carry +1 goods; the Stewards can build a Mill.', { caravanGoods: 1 }, { cost: 6, needs: ['pottery'] }),
  coinage: T(2, 'Coinage', 'trade', 'A market takes one more shift a day.', { marketCap: 1 }, { cost: 3, needs: ['currency'] }),
  calendar: T(2, 'The Calendar', 'culture', 'A feast, and every song, gives +1 happiness more.', { festivalJoy: 1 }, { cost: 9, needs: ['writing'] }),
  medicine: T(2, 'Medicine', 'any', 'Removes Sickness; fevers are 3 in 10 less likely.', { fever: -3 }, { cost: 3, needs: ['writing'], removes: ['sickness'] }),
  // Matt, after playtest 10 (12 of the Harvest's food spoiled against a cap
  // of 6, with Granaries just learned): "it should lift the cap. That'll be
  // more fun."
  granaries: T(2, 'Granaries', 'trade', 'The stores keep 4 more food overnight, and a famine costs 1 less happiness.', { larder: 4, famineHappiness: -1 }, { needs: ['irrigation'] }),
  mathematics: T(2, 'Mathematics', 'science', 'One more worker may study each day.', { studyCap: 1 }, { needs: ['writing'] }),
  // THE LADDER (2026-10-15): each of these teaches the Stewards to build the
  // machine the next tier is made in. `late` so old seeds keep their reactions.
  kilns: T(2, 'Kilns', 'science', 'The Stewards can build a Kiln. Every tier 3 technology is made from something it fires.', {}, { needs: ['pottery'], late: true }),
  law: T(2, 'Law', 'culture', 'A levy costs no happiness; workers halve only at happiness 2 or less (not 3); unlocks the Law of the Watch.', { levyHappiness: 1, unhappyAt: -1 }, { cost: 9, needs: ['rituals', 'writing'] }),

  // ---- tier 3: the commonwealth comes of age
  roads: T(3, 'Roads', 'trade', 'Paved roads: 3 hexes an action, joined villages send +2, +1 caravan a day, rough ground costs 1.', { roadLevel: 1, caravanCap: 1, marchRange: 1 }, { cost: 11, needs: ['wheel', 'paths'] }),
  iron: T(3, 'Iron', 'military', 'Units and defence dice hit on 2+; warbands may grow 1 stronger.', { hitOn: -1, bandMax: 1 }, { cost: 11, needs: ['bronze'] }),
  crop_rotation: T(3, 'Crop Rotation', ['military', 'trade'], 'Frontier: +1 on every die a unit rolls working the ground, and on Hunt near the walls. Stewards: a staffed field grows +1 food.', { huntBase: 1 }, { needs: ['irrigation', 'granaries'] }),
  architecture: T(3, 'Architecture', 'any', 'The great work costs 1 less worker and 1 less coin per stage.', { buildWorkers: -1, buildCoin: -1 }, { cost: 5, needs: ['masonry', 'mathematics'] }),
  philosophy: T(3, 'Philosophy', 'culture', 'A rally gives +1 happiness more.', { rally: 1 }, { cost: 6, needs: ['calendar', 'mathematics'] }),
  astronomy: T(3, 'Astronomy', 'science', 'Ponder yields +1 knowledge.', { ponder: 1 }, { needs: ['calendar'] }),
  drama: T(3, 'Drama', 'culture', 'A feast, and every song, gives +1 happiness more.', { festivalJoy: 1 }, { cost: 8, needs: ['rituals', 'calendar'] }),
  herbalism: T(3, 'Herbalism', 'any', 'Fevers are another 2 in 10 less likely, and a healthier town draws families: one arrives on a roll 1 lower.', { fever: -2, births: 1 }, { cost: 6, needs: ['medicine'] }),
  distilling: T(3, 'Distilling', 'science', 'The Stewards can build a Still. Every tier 4 technology is made from something it draws off.', {}, { cost: 5, needs: ['kilns'], late: true }),
  banking: T(3, 'Banking', 'trade', 'Markets pay +1 coin more.', { marketCoin: 1 }, { cost: 6, needs: ['coinage', 'mathematics'] }),

  // ---- tier 4: wonders of the age
  engineering: T(4, 'Engineering', 'any', 'The great work costs 1 less goods and 1 less coin per stage, and the city throws one more die at raiders.', { buildGoods: -1, buildCoin: -1, garrisonDice: 1 }, { cost: 7, needs: ['architecture', 'roads'] }),
  highways: T(4, 'Highways', 'trade', 'Roads become HIGHWAYS: 4 hexes an action along them, joined villages send +3, and one more caravan may go.', { roadLevel: 1, caravanCap: 1 }, { cost: 7, needs: ['roads'], late: true }),
  navigation: T(4, 'Navigation', 'military', 'Your units see 1 hex further, and goods come in by water: +1 goods every night.', { sight: 1, goodsNight: 1 }, { cost: 7, needs: ['roads', 'astronomy'] }),
  steel: T(4, 'Steel', 'military', 'The city rolls +1 die against raiders; units fight with +1 die.', { garrisonDice: 1, bandDice: 1 }, { cost: 13, needs: ['iron', 'palisades'] }),
  literature: T(4, 'Literature', 'culture', 'A feast, and every song, gives +1 happiness more.', { festivalJoy: 1 }, { cost: 10, needs: ['drama', 'philosophy'] }),
  assaying: T(4, 'Assaying', 'science', 'The Stewards can build a Crucible. Every tier 5 technology is made from something it melts.', {}, { cost: 7, needs: ['distilling'], late: true }),
  universities: T(4, 'Universities', 'science', 'Study yields +1 more, and one more may study.', { study: 1, studyCap: 1 }, { cost: 10, needs: ['philosophy', 'astronomy'] }),

  // ---- tier 5: the long game (2026-09-21, Matt: "expanding how much
  // technology there is... go beyond the complexity that we already have")
  aqueducts: T(5, 'Aqueducts', ['trade', 'military'], 'Stewards: the larder keeps 6 more food, and a staffed field grows +1 food. Frontier: +1 on every die a unit rolls working the ground, and on Hunt near the walls.', { larder: 6, huntBase: 1 }, { cost: 10, needs: ['engineering', 'crop_rotation'] }),
  printing: T(5, 'Printing', 'science', 'Every night, +1 knowledge, and Study yields +1 more.', { nightKnowledge: 1, study: 1 }, { cost: 15, needs: ['literature', 'universities'] }),
  standing_army: T(5, 'The Standing Army', 'military', 'A warband may grow two stronger, and the city throws one more die at raiders.', { bandMax: 2, garrisonDice: 1 }, { needs: ['steel'] }),
  // (Its marchRange went: Roads already buys rough ground back, and a second
  // point did nothing - the tech audit, playtest 10.)
  cartography: T(5, 'Cartography', 'military', 'Your units see 1 hex further, and the maps teach: +1 knowledge every night.', { sight: 1, nightKnowledge: 1 }, { cost: 13, needs: ['navigation'] }),
  // (Hiring craftsmen was dropped after playtest 11; Guilds pays at the stall instead.)
  guilds: T(5, 'Guilds', 'trade', 'Markets pay +1 coin, a market takes one more shift a day, and the guilds turn out +1 goods every night.', { marketCoin: 1, marketCap: 1, goodsNight: 1 }, { cost: 12, needs: ['banking'] }),
  civil_service: T(5, 'The Civil Service', 'culture', 'A levy or edict brings +1 worker.', { levyWorkers: 1, unrest: -2 }, { cost: 14, needs: ['universities', 'banking'] }),
};

// ---- BRANCHES: not in place at the start. Other roles open them - a region
// found, a storyline finished, the town reaching a state at night - and
// then Science has somewhere new to climb. Matt: "discoveries from other
// players add branches to this tree... the culture player achieves
// something and tells the science player about it."
export const BRANCHES = {
  seafaring: { name: 'Seafaring', text: 'Boats and nets.', opens: 'finding the Marsh, or the fishers\' luck' },
  mining: { name: 'Mining', text: 'Ore, and what to make of it.', opens: 'finding the Copper Hills' },
  husbandry: { name: 'Husbandry', text: 'Flocks and herds.', opens: 'finding the Fertile Valley, or the wolf pelts' },
  old_knowledge: { name: 'Old Knowledge', text: 'What the people before us knew.', opens: 'finding the Ruins, or opening what was buried' },
  statecraft: { name: 'Statecraft', text: 'Laws and government.', opens: 'the first law, the neighbours, or seven people at a night' },
  arts: { name: 'The Arts', text: 'Songs, plays and festivals.', opens: 'happiness 8 at a night, the players, or the comet read as a good sign' },
};
export const BRANCH_ORDER = Object.keys(BRANCHES);

const B = (branch, tier, name, forRole, text, mods = {}, extra = {}) => ({ ...T(tier, name, forRole, text, mods, extra), branch });
Object.assign(TECHS, {
  // seafaring
  boatbuilding: B('seafaring', 2, 'Boatbuilding', ['military', 'trade'], 'Frontier: +1 on every die a unit rolls working the ground, and on Hunt near the walls. Stewards: a staffed field grows +1 food.', { huntBase: 1 }, { cost: 7 }),
  nets: B('seafaring', 2, 'Nets', 'military', 'Your units roll one more die working the ground, and the nets bring +1 food every night.', { huntDice: 1, foodNight: 1 }, { cost: 7 }),
  sailing: B('seafaring', 3, 'Sailing', 'trade', 'Caravans carry +1 goods and one more may go; markets pay +1 coin.', { caravanGoods: 1, caravanCap: 1, marketCoin: 1 }, { cost: 7, needs: ['boatbuilding'] }),
  lighthouse: B('seafaring', 4, 'The Lighthouse', 'any', 'Camps and dens send raiders only every other night.', { menaceSlow: 1 }, { needs: ['sailing'] }),
  astrolabe: B('seafaring', 5, 'The Astrolabe', 'trade', 'One more caravan may go each day, your units see one further, and the stars teach: +1 knowledge every night.', { caravanCap: 1, sight: 1, nightKnowledge: 1 }, { cost: 14, needs: ['lighthouse'] }),
  // mining
  smelting: B('mining', 2, 'Smelting', 'trade', 'Markets pay +1 coin.', { marketCoin: 1 }, { cost: 6 }),
  quarrying: B('mining', 2, 'Quarrying', 'any', 'The great work costs 1 less goods per stage, and stone walls: the city throws one more die at raiders.', { buildGoods: -1, garrisonDice: 1 }, { cost: 6 }),
  metalwork: B('mining', 3, 'Metalwork', 'military', 'Your units fight with one extra die.', { bandDice: 1 }, { cost: 10, needs: ['smelting'] }),
  mint: B('mining', 4, 'The Mint', 'trade', 'Markets pay +2 coin more.', { marketCoin: 2 }, { cost: 8, needs: ['smelting', 'quarrying'] }),
  deep_mines: B('mining', 5, 'Deep Mines', 'trade', 'Every night, +2 goods.', { goodsNight: 2 }, { cost: 12, needs: ['mint'] }),
  // husbandry
  herding: B('husbandry', 2, 'Herding', 'military', 'Every hunting and farming roll +1.', { huntBase: 1 }, { cost: 8 }),
  wool: B('husbandry', 3, 'Wool', 'trade', 'Buy grain brings +1 food, caravans carry +1 goods, and wool sells: markets pay +1 coin.', { grainFood: 1, caravanGoods: 1, marketCoin: 1 }, { cost: 7, needs: ['herding'] }),
  breeding: B('husbandry', 4, 'Breeding', 'culture', 'A family arrives on a roll 2 lower.', { births: 2 }, { cost: 8, needs: ['herding'] }),
  great_herds: B('husbandry', 5, 'The Great Herds', 'trade', 'Every night, +1 food; a family arrives on 4+ instead of 5+.', { foodNight: 1, births: 1 }, { needs: ['breeding', 'wool'] }),
  // old knowledge
  antiquities: B('old_knowledge', 2, 'Antiquities', 'science', 'Pondering yields +1 knowledge.', { ponder: 1 }),
  lost_engineering: B('old_knowledge', 3, 'Lost Engineering', 'any', 'The great work costs 1 less worker and 1 less goods per stage.', { buildWorkers: -1, buildGoods: -1 }, { cost: 5, needs: ['antiquities'] }),
  the_old_tongue: B('old_knowledge', 4, 'The Old Tongue', 'science', 'Study yields +2 knowledge.', { study: 2 }, { cost: 13, needs: ['antiquities'] }),
  the_old_rites: B('old_knowledge', 5, 'The Old Rites', 'culture', 'A feast, and every song, gives +1 happiness more, and fevers are 2 in 10 less likely.', { festivalJoy: 1, fever: -2 }, { cost: 10, needs: ['the_old_tongue', 'lost_engineering'] }),
  // statecraft
  bureaucracy: B('statecraft', 2, 'Bureaucracy', 'culture', 'A levy or edict brings +1 worker more.', { levyWorkers: 1 }, { cost: 10 }),
  taxation: B('statecraft', 3, 'Taxation', 'trade', 'A market takes one more shift a day, and markets pay +1.', { marketCap: 1, marketCoin: 1 }, { needs: ['bureaucracy'] }),
  diplomacy: B('statecraft', 4, 'Diplomacy', 'any', 'Parley costs half; camps and dens send raiders only every other night.', { parleyHalf: 1, menaceSlow: 1 }, { cost: 12, needs: ['taxation'] }),
  federation: B('statecraft', 5, 'Federation', ['culture', 'military'], 'Envoys and parleys warm a rival 1 step more, and allies send tribute: +1 food and +1 goods every night.', { envoyTwice: 1, unrest: -2, foodNight: 1, goodsNight: 1 }, { cost: 15, needs: ['diplomacy'] }),
  // the arts
  music: B('arts', 2, 'Music', 'culture', 'A feast, and every song, gives +1 happiness more.', { festivalJoy: 1 }, { cost: 8 }),
  theatre: B('arts', 3, 'Theatre', 'culture', 'A rally gives +1 happiness more.', { rally: 1 }, { cost: 5, needs: ['music'] }),
  poetry: B('arts', 4, 'Poetry', 'culture', 'A feast, and every song, gives +1 happiness more.', { festivalJoy: 1 }, { cost: 10, needs: ['theatre'] }),
  opera: B('arts', 5, 'Opera', 'culture', 'A feast, and every song, gives +2 happiness more; a song seeds its card twice.', { festivalJoy: 2, songTwice: 1 }, { cost: 15, needs: ['poetry'] }),
});

export const TECH_ORDER = Object.keys(TECHS);
export const TECH_TIERS = [1, 2, 3, 4, 5];

const has = (civ, id) => (civ?.techs || []).includes(id);
export const branchOpen = (civ, branch) => !branch || (civ?.branches || []).includes(branch);
/** Prerequisites all known, its branch open (if any), and not yet known itself. */
/** Held theories (the night settled them). */
export const theoriesHeld = (civ) => Object.keys(civ?.lore?.settled || {}).length;
// (Tier 5 used to wait on held theories on the deep bench. The machines
// gate the deep tiers now: nothing above tier 2 can be made without the
// right one standing in the city.)
export const techReachable = (civ, id) => Boolean(TECHS[id]) && !has(civ, id)
  && branchOpen(civ, TECHS[id].branch) && TECHS[id].needs.every((n) => has(civ, n));
/** The techs Science can see at all: the trunk, plus every open branch. */
export const techVisible = (civ, id) => Boolean(TECHS[id]) && branchOpen(civ, TECHS[id].branch);
// KNOWLEDGE BUYS A TECHNOLOGY AGAIN (2026-09-21). Finding the reaction is
// the Scholars' puzzle; writing it down costs what the tree says, and the
// governments' `techCost` means something once more. `techPrice` in
// roleActions.js adds the modifiers.
export const techCost = (id) => TECHS[id]?.cost ?? 3;

/** The sum of every known tech's mods. */
export const techMods = (civ) => (civ?.techs || []).reduce((acc, id) => {
  Object.entries(TECHS[id]?.mods || {}).forEach(([k, v]) => { acc[k] = (acc[k] || 0) + v; });
  return acc;
}, {});

// Fevers (Sickness, Marsh Fever, Something Stirs, the Dark Prophecy) all
// roll against the misery; Medicine and Herbalism shorten those odds - and
// so do the Law of the Well, the Chiefdom and the Council of Elders, which
// promised it for a week before anything read them (playtest by machine,
// 2026-09-21: only the tree's `fever` was summed).
export const feverChance = (civ, base) => Math.max(0, base
  + (techMods(civ).fever || 0) + (governmentMods(civ).fever || 0) + (lawMods(civ).fever || 0));

// ---------------------------------------------------------------- wonders
//
// THERE IS NO END TO REACH. Matt, 2026-09-21: "I'm not sure that a game like
// this needs goals exactly... winning is something that will take place at
// the very far future... maybe we should introduce wonders in some way and
// they give abilities." So the Vision offers two WONDERS; the group picks one
// to raise; every role can put a turn into a stage; and when the last stage
// stands the wonder GRANTS something for the rest of the game - read through
// modsOf like a tech - and the next Vision is dealt. The table still calls
// the thing in progress "the great work", and `civ.monument` keeps its name
// in saves.
//
// Story chapters offer wonders of their own (`story: true`); the Vision
// never deals those.

export const GOALS = {
  monument: {
    name: 'The Great Monument',
    text: 'Three stages of stone in the square. Finished, every feast and song gives +1 happiness more, for good.',
    build: { label: 'Raise a monument stage', stages: 3, cost: { goods: 2, coin: 4, workers: 2 } },
    grants: { festivalJoy: 1 },
    done: (civ) => (civ.monument?.stages ?? 0) >= 3,
    progress: (civ) => `${civ.monument?.stages ?? 0}/3 stages`,
    ending: ['The Monument Stands', 'Three stages of stone in the square.'],
  },
  walls: {
    name: 'The Great Wall',
    // (Each section used to take a point off the strongest camp; camps have
    // had fixed strength since 2026-09-20 and the line was a relic - playtest 13.)
    text: 'Ring the town in stone: three sections, any role can raise one. Finished, the city throws two more dice at any raiders who reach it, for good.',
    build: { label: 'Raise a wall section', stages: 3, cost: { goods: 3, coin: 2, workers: 2 } },
    grants: { garrisonDice: 2 },
    done: (civ) => (civ.monument?.stages ?? 0) >= 3,
    progress: (civ) => `${civ.monument?.stages ?? 0}/3 sections`,
    ending: ['The Wall Stands', 'Three sections of wall round the town.'],
  },
  academy: {
    name: 'The Academy',
    text: 'A house of learning: three wings. Finished, the Scholars run one more experiment a day and every Study yields one more.',
    build: { label: 'Raise an academy wing', stages: 3, cost: { goods: 2, coin: 5, workers: 2 } },
    grants: { mixes: 1, study: 1 },
    done: (civ) => (civ.monument?.stages ?? 0) >= 3,
    progress: (civ) => `${civ.monument?.stages ?? 0}/3 wings`,
    ending: ['The Academy', 'Three wings, open to students.'],
  },
  harbour: {
    name: 'The Harbour',
    text: 'Stone quays: three stages. Finished, one more caravan may go each day, each carries one more, and markets pay one more coin.',
    build: { label: 'Raise a harbour stage', stages: 3, cost: { goods: 3, coin: 4, workers: 2 } },
    grants: { caravanCap: 1, caravanGoods: 1, marketCoin: 1 },
    done: (civ) => (civ.monument?.stages ?? 0) >= 3,
    progress: (civ) => `${civ.monument?.stages ?? 0}/3 stages`,
    ending: ['The Harbour', 'Stone quays, and ships at them.'],
  },
  // ---- the later wonders (2026-09-25: "make sure the game can last long enough. It's a very long game.")
  // Four stages and dearer, so they carry a long game and there are enough
  // left to build after the rivals have taken a few.
  lighthouse: {
    era: 2,
    name: 'The Lighthouse',
    text: 'A lighthouse on the headland: four stages. Finished, every unit sees one hex further.',
    build: { label: 'Raise a lighthouse stage', stages: 4, cost: { goods: 3, coin: 4, workers: 2 } },
    grants: { sight: 1 },
    done: (civ) => (civ.monument?.stages ?? 0) >= 4,
    progress: (civ) => `${civ.monument?.stages ?? 0}/4 stages`,
    ending: ['The Lighthouse', 'Its fire can be seen from the far shore.'],
  },
  great_granary: {
    era: 2,
    name: 'The Great Granary',
    text: 'Stone bins: four stages. Finished, the larder keeps 6 more food and every grain bought brings one more.',
    build: { label: 'Raise a granary stage', stages: 4, cost: { goods: 4, coin: 3, workers: 2 } },
    grants: { larder: 6, grainFood: 1 },
    done: (civ) => (civ.monument?.stages ?? 0) >= 4,
    progress: (civ) => `${civ.monument?.stages ?? 0}/4 stages`,
    ending: ['The Great Granary', 'Stone bins, and a larder that keeps.'],
  },
  arena: {
    era: 2,
    name: 'The Arena',
    text: 'An arena: four stages. Finished, every festival gives one more happiness.',
    build: { label: 'Raise an arena stage', stages: 4, cost: { goods: 4, coin: 5, workers: 2 } },
    grants: { festivalJoy: 1 },
    done: (civ) => (civ.monument?.stages ?? 0) >= 4,
    progress: (civ) => `${civ.monument?.stages ?? 0}/4 stages`,
    ending: ['The Arena', 'The whole town fits in it.'],
  },
  great_library: {
    era: 2,
    name: 'The Great Library',
    text: 'A library: four stages. Finished, one more knowledge every night and every Study yields one more.',
    build: { label: 'Raise a library stage', stages: 4, cost: { goods: 3, coin: 6, workers: 2 } },
    grants: { nightKnowledge: 1, study: 1 },
    done: (civ) => (civ.monument?.stages ?? 0) >= 4,
    progress: (civ) => `${civ.monument?.stages ?? 0}/4 stages`,
    ending: ['The Great Library', 'Every scroll on the island, in one place.'],
  },
  sky_temple: {
    era: 2,
    name: 'The Temple of the Sky',
    text: 'A temple on the hill above the town: four stages. Finished, every feast and song gives +1 happiness more, for good.',
    build: { label: 'Raise a temple stage', stages: 4, cost: { goods: 5, coin: 5, workers: 2 } },
    grants: { festivalJoy: 1 },
    done: (civ) => (civ.monument?.stages ?? 0) >= 4,
    progress: (civ) => `${civ.monument?.stages ?? 0}/4 stages`,
    ending: ['The Temple of the Sky', 'A temple on the hill above the town.'],
  },
  kings_road: {
    era: 2,
    name: 'The King\'s Road',
    text: 'A paved road: four stages. Finished, every road is one level better.',
    build: { label: 'Lay a stage of the King\'s Road', stages: 4, cost: { goods: 4, coin: 4, workers: 2 } },
    grants: { roadLevel: 1 },
    done: (civ) => (civ.monument?.stages ?? 0) >= 4,
    progress: (civ) => `${civ.monument?.stages ?? 0}/4 stages`,
    ending: ['The King\'s Road', 'Paved from the square to the edge of the map.'],
  },
  war_college: {
    era: 2,
    name: 'The War College',
    text: 'A war college: four stages. Finished, a warband may grow one stronger.',
    build: { label: 'Raise a college stage', stages: 4, cost: { goods: 5, coin: 4, workers: 2 } },
    grants: { bandMax: 1 },
    done: (civ) => (civ.monument?.stages ?? 0) >= 4,
    progress: (civ) => `${civ.monument?.stages ?? 0}/4 stages`,
    ending: ['The War College', 'The commonwealth trains its own captains.'],
  },
  colossus: {
    era: 2,
    name: 'The Colossus',
    text: 'A bronze statue at the harbour mouth: four stages. Finished, markets pay one more coin and caravans carry one more.',
    build: { label: 'Raise a colossus stage', stages: 4, cost: { goods: 5, coin: 6, workers: 2 } },
    grants: { marketCoin: 1, caravanGoods: 1 },
    done: (civ) => (civ.monument?.stages ?? 0) >= 4,
    progress: (civ) => `${civ.monument?.stages ?? 0}/4 stages`,
    ending: ['The Colossus', 'A bronze giant at the harbour mouth.'],
  },
};
Object.assign(GOALS, {
  garden: {
    story: true, name: 'A Garden of the Valley',
    text: 'Channel the spring through the whole valley: two stages. Finished, every farming roll is +1 and the larder holds 6 more.',
    build: { label: 'Dig the garden', stages: 2, cost: { goods: 1, coin: 2, workers: 2 } },
    grants: { huntBase: 1, larder: 6 },
    done: (civ) => (civ.monument?.stages ?? 0) >= 2,
    progress: (civ) => `${civ.monument?.stages ?? 0}/2 stages`,
    ending: ['The Garden', 'The valley is irrigated from the spring to the shore.'],
  },
  forum: {
    story: true, name: 'The Forum',
    text: 'A place to argue instead of fight: two stages. Finished, the streets are calmer, a levy costs no happiness, and the people keep working until happiness 2.',
    build: { label: 'Build the forum', stages: 2, cost: { goods: 2, coin: 2, workers: 1 } },
    grants: { unrest: -2, levyHappiness: 1, unhappyAt: -1 },
    done: (civ) => (civ.monument?.stages ?? 0) >= 2,
    progress: (civ) => `${civ.monument?.stages ?? 0}/2 stages`,
    ending: ['The Forum', 'A place to argue instead of fight.'],
  },
  great_hall: {
    story: true, name: 'The Great Hall',
    text: 'A roof big enough for two peoples: two stages. Finished, a levy brings one more worker and families arrive more readily.',
    build: { label: 'Raise the great hall', stages: 2, cost: { goods: 2, coin: 2, workers: 2 } },
    grants: { levyWorkers: 1, births: 1 },
    done: (civ) => (civ.monument?.stages ?? 0) >= 2,
    progress: (civ) => `${civ.monument?.stages ?? 0}/2 stages`,
    ending: ['The Great Hall', 'One roof for two peoples.'],
  },
  observatory: {
    story: true, name: 'The Observatory',
    text: 'A tower to watch the sky from: two stages. Finished, your units see one further, pondering yields one more, and the night brings +1 knowledge.',
    build: { label: 'Raise the tower', stages: 2, cost: { goods: 2, coin: 3, workers: 2 } },
    grants: { sight: 1, ponder: 1, nightKnowledge: 1 },
    done: (civ) => (civ.monument?.stages ?? 0) >= 2,
    progress: (civ) => `${civ.monument?.stages ?? 0}/2 stages`,
    ending: ['The Observatory', 'A tower, and a map of the sky.'],
  },
  archive: {
    story: true, name: 'The Archive',
    text: 'A stone archive of the old knowledge: two stages. Finished, three properties are read out at once and Study yields one more.',
    build: { label: 'Build the archive', stages: 2, cost: { goods: 1, coin: 3, workers: 2 }, onBuilt: { reveal: 3 } },
    grants: { study: 1 },
    done: (civ) => (civ.monument?.stages ?? 0) >= 2,
    progress: (civ) => `${civ.monument?.stages ?? 0}/2 stages`,
    ending: ['The Archive', 'The old knowledge, written down.'],
  },
});
export const WONDERS = GOALS;
// The Vision offers only the trunk wonders; story wonders arrive by story.
export const GOAL_ORDER = Object.keys(GOALS).filter((id) => !GOALS[id].story);
export const GOALS_OFFERED = 2;
/** Wonders the town has raised. */
export const wondersOf = (civ) => civ?.wonders || [];
/** What the raised wonders grant, summed - a wonder is one entry, like a tech. */
export const wonderMods = (civ) => wondersOf(civ).reduce((acc, id) => {
  Object.entries(GOALS[id]?.grants || {}).forEach(([k, v]) => { acc[k] = (acc[k] || 0) + v; });
  return acc;
}, {});
/** Trunk wonders still to be offered: not raised, not the one in progress. */
export const wondersLeft = (civ) => GOAL_ORDER.filter((id) => !wondersOf(civ).includes(id) && civ?.goal !== id);
// story goal choices reference GOALS text lazily
Object.values(EVENTS).forEach((e) => (e.choices || []).forEach((c) => { if (typeof c.text === 'function') c.text = c.text(); }));

// The goal the era is playing for: chosen, or (older saves) the monument
// once its card turned.
export const goalOf = (civ) => civ?.goal || (civ?.monument?.revealed ? 'monument' : null);

// -------------------------------------------------------------- regions
//
// The map, for now, is a deck: each Explore reveals the next region, pays
// its bonus, and drops its echo card into the event deck near the top.

export const REGIONS = {
  fertile_valley: { name: 'Fertile Valley', text: 'Rich soil.', bonus: { food: 3 }, adds: 'valley_harvest', unlocks: 'husbandry', reveals: ['growth', 'moisture'] },
  copper_hills: { name: 'Copper Hills', text: 'Copper in the rock.', bonus: { goods: 2 }, adds: 'ore_find', unlocks: 'mining', reveals: ['metal', 'weight'] },
  old_road: { name: 'The Old Road', text: 'A paved road from before the town.', bonus: { coin: 2 }, adds: 'merchants', reveals: ['number', 'weight'] },
  neighbours: { name: 'The Neighbours', text: 'Smoke over the ridge: another people.', bonus: { knowledge: 1 }, adds: 'envoys', meets: true, unlocks: 'statecraft', reveals: ['word', 'heat'] },
  ruins: { name: 'The Ruins', text: 'Carved stones taller than houses.', bonus: { knowledge: 2 }, adds: 'omen_of_ruins', unlocks: 'old_knowledge', reveals: ['stone', 'weight'] },
  marsh: { name: 'The Marsh', text: 'Reeds, mist and biting insects.', bonus: { goods: 1 }, adds: 'marsh_fever', unlocks: 'seafaring', reveals: ['water', 'moisture'] },
  // THE FAR RING (the bigger island, after playtest 12): seven hexes out,
  // the second year's map. Richer finds for the longer walk.
  great_forest: { name: 'The Great Forest', text: 'Old, deep forest.', bonus: { goods: 4 }, reveals: ['growth', 'heat'] },
  salt_flats: { name: 'The Salt Flats', text: 'Salt to the horizon, worth carrying.', bonus: { coin: 5 }, reveals: ['water', 'weight'] },
  high_pass: { name: 'The High Pass', text: 'A pass in the mountains with a view of half the island.', bonus: { knowledge: 3 }, reveals: ['wind', 'weight'] },
  the_lake: { name: 'The Lake', text: 'A deep, cold lake full of fish.', bonus: { food: 6 }, reveals: ['water', 'heat'] },
  old_battlefield: { name: 'The Old Battlefield', text: 'Rust and bones in the grass.', bonus: { goods: 2, coin: 2 }, reveals: ['metal', 'heat'] },
  the_cliffs: { name: 'The Cliffs', text: 'Sea cliffs, and birds by the thousand.', bonus: { food: 2, knowledge: 2 }, reveals: ['stone', 'moisture'] },
};

export const REGION_ORDER = ['fertile_valley', 'copper_hills', 'old_road', 'neighbours', 'ruins', 'marsh'];
/** The far ring's regions, dealt to sites 6-11. */
export const FAR_REGION_ORDER = ['great_forest', 'salt_flats', 'high_pass', 'the_lake', 'old_battlefield', 'the_cliffs'];

// The six directions Military can explore in. Each game deals one region
// to each direction, sight unseen (`civ.regionSites`); Explore picks a
// direction and finds whatever is there. Matt, playtest 4: "I don't know
// what spot it reveals... exploring should expand the map, we should find
// new stuff." Index = the DIRS index in world/layout.js.
export const DIRECTIONS = ['east', 'north-east', 'north-west', 'west', 'south-west', 'south-east'];

// Older saves have no `regionSites`; they used the fixed layout of v0.2.
export const DEFAULT_SITES = { 0: 'fertile_valley', 1: 'copper_hills', 2: 'ruins', 3: 'neighbours', 4: 'marsh', 5: 'old_road' };
export const sitesOf = (civ) => civ?.regionSites || DEFAULT_SITES;
export const directionOf = (civ, regionId) => Number(Object.entries(sitesOf(civ)).find(([, id]) => id === regionId)?.[0] ?? -1);

// ================================================================ THE YEAR
// (DESIGN.md "The Year", 2026-09-27.) The seasons live in the DECK, not in
// the rules: nobody's turn changes in any month, only the world's card.
// Each year the free months are dealt from that month's season's pool, so
// the table learns that summer is when trouble comes and winter is when the
// wolves do. Three months are fixed: the year card, the Harvest, Midwinter.

// Which season each base card belongs to. A card not listed can turn in any
// month. (Names are the reason: a Late Frost is spring, Locusts are summer.)
export const CARD_SEASON = {
  rain: 'spring', late_frost: 'spring', wanderers: 'spring', teacher: 'spring', census: 'spring',
  sun: 'summer', drought: 'summer', drought2: 'summer', locusts: 'summer', raiders: 'summer', raiders2: 'summer', bandits: 'summer', fire: 'summer', players: 'summer',
  harvest: 'autumn', fishers_luck: 'autumn', restless: 'autumn', dispute: 'autumn', petition: 'autumn',
  cold: 'winter', mild_winter: 'winter', wolves: 'winter', sickness: 'winter', comet: 'winter', eclipse: 'winter',
};
// THE AGES (Matt, after playtest 12: "ages sounds like a cool idea,
// especially if we could do graphic changes"). A town crosses into the next
// when it knows three technologies of that age's tier - learned, not
// bought. Each age turns its own card the month after the crossing, changes
// how the town looks on both screens (world/layout.js), and adds its own
// troubles and chances to the deck from then on.
export const AGES = [
  { id: 'founding', name: 'The Founding', tier: 0 },
  { id: 'bronze', name: 'The Bronze Age', tier: 2 },
  { id: 'iron', name: 'The Iron Age', tier: 3 },
  { id: 'classical', name: 'The Classical Age', tier: 4 },
];
export const AGE_NEEDS = 3; // technologies of the age's tier (or higher)
/** The age the town's technologies have reached (0 = the Founding). */
export const ageOf = (civ) => {
  const techs = civ?.techs || [];
  let age = 0;
  AGES.forEach((a, i) => { if (i && techs.filter((t) => (TECHS[t]?.tier || 0) >= a.tier).length >= AGE_NEEDS) age = i; });
  return age;
};
/** Cards each age adds to the deck, from its crossing on. */
export const AGE_POOLS = {
  1: ['smiths_fair', 'bronze_raiders', 'the_trade_road'],
  2: ['iron_war_band', 'the_forge', 'tax_revolt'],
  3: ['the_games', 'philosophers', 'great_fire'],
};
export const ALL_POOL_CARDS = [...Object.values(AGE_POOLS).flat()];
export const seasonPool = (season, removed = [], age = 0) => [...BASE_POOL, ...Object.entries(AGE_POOLS).filter(([a]) => Number(a) <= age).flatMap(([, ids]) => ids)]
  .filter((id) => !removed.includes(id) && (CARD_SEASON[id] || season) === season);

Object.assign(EVENTS, {
  // THE AGES: the crossing's own card, then each age's troubles and chances.
  age_bronze: { kind: 'age', name: 'The Bronze Age', text: 'Bronze: copper and tin, harder than either. The smiths are busy and the walls go up in stone.', effect: { happiness: 1, goods: 2 } },
  age_iron: { kind: 'age', name: 'The Iron Age', text: 'Iron is cheap now, and so are weapons. The houses are stone and the towers taller.', effect: { happiness: 1, coin: 2 } },
  age_classical: { kind: 'age', name: 'The Classical Age', text: 'Laws, schools, theatres, and arguments in the square.', effect: { happiness: 1, knowledge: 2 } },
  smiths_fair: {
    kind: 'civic', name: 'The Smiths\' Fair', text: 'Smiths from every valley, selling bronze.',
    choices: [
      { id: 'buy', label: 'Buy their tools (3 coin)', requires: { coin: 3 }, effect: { coin: -3, goods: 5 } },
      { id: 'sell', label: 'Sell them grain (3 food)', requires: { food: 3 }, effect: { food: -3, coin: 4 } },
    ],
  },
  bronze_raiders: { kind: 'people', name: 'Raiders with Bronze', text: 'Raiders with bronze swords make a strong camp.', spawn: { kind: 'camp', strength: 3 } },
  the_trade_road: { kind: 'people', name: 'Traders on the Road', text: 'A string of mules. The traders pay to rest here.', effect: { coin: 3 } },
  iron_war_band: { kind: 'people', name: 'An Iron War Band', text: 'A war band in mail makes camp and means to stay.', spawn: { kind: 'camp', strength: 4 } },
  the_forge: {
    kind: 'civic', name: 'The Forge', text: 'A new forge. What should it make?',
    choices: [
      { id: 'tools', label: 'Tools for the workshops', effect: { goods: 4 } },
      { id: 'sell', label: 'Iron for the traders', effect: { coin: 4 } },
    ],
  },
  tax_revolt: {
    kind: 'civic', name: 'The Tax Revolt', text: 'People in the square say the Hall takes too much.',
    choices: [
      { id: 'pay', label: 'Pay them back (3 coin)', requires: { coin: 3 }, effect: { coin: -3, happiness: 1 } },
      { id: 'refuse', label: 'The Hall needs it', effect: { happiness: -1 } },
    ],
  },
  the_games: {
    kind: 'civic', name: 'The Games', text: 'Races and wrestling, and a crowd to feed.',
    choices: [
      { id: 'hold', label: 'Hold them (3 food, 2 coin)', requires: { food: 3, coin: 2 }, effect: { food: -3, coin: -2, happiness: 3 } },
      { id: 'skip', label: 'Not this year', effect: {} },
    ],
  },
  philosophers: { kind: 'people', name: 'The Philosophers', text: 'Three old men who will not stop asking why. The Scholars learn from them.', effect: { knowledge: 3 } },
  great_fire: { kind: 'omen', name: 'The Great Fire', text: 'A lamp, a dry night, and a street of timber houses burns.', effect: { goods: -3, happiness: -1 } },
  // THE PEOPLE (engine/demands.js): they speak up, the table picks which of
  // two things it will promise, and on a month nobody names they ask.
  the_people_speak: {
    kind: 'people', name: 'The People Speak',
    text: 'A crowd in the square with two demands. Promise one; they will come asking for it within a few months.',
    reveal: 'demandOffer',
  },
  the_people_ask: {
    kind: 'people', name: 'The People Ask',
    text: 'The crowd is back: did you do what you promised?',
    reveal: 'demandAsk',
  },
  // A FAMILY AT THE GATE (Matt, 2026-09-28: "a family arriving should
  // present an option: take them as population or take them as workers").
  // The night rolls for whether one comes; the table decides what to do.
  a_family: {
    kind: 'people', name: 'A Family at the Gate',
    text: 'A family with a cart and three children. Take them in for good, or put them to work for a season.',
    choices: [
      { id: 'in', label: 'Take them in', text: '+1 person, for good. They eat 3 food settling in.', effect: { population: 1, food: -3 } },
      { id: 'work', label: 'Put them to work', text: '+3 workers at the next dawn. Then they move on.', extraWorkers: 3 },
    ],
  },
  // The first month of every year: the session's goal, one of two.
  year_card: {
    kind: 'year', name: 'The Year Ahead',
    text: 'A new year. Name one thing the town means to do before winter. Choose together; met by Midwinter, it is remembered.',
    reveal: 'yearGoal',
  },
  // September, every year: what the fields and the villages bring in.
  the_harvest: {
    kind: 'harvest', name: 'The Harvest',
    text: 'Every field and village sends its harvest at once. What the granary cannot hold spoils: spend it, send it, or lose it.',
    harvest: true,
  },
  // December, every year: the feast, the tale, the chronicle, the end of the sitting.
  midwinter: {
    kind: 'midwinter', name: 'Midwinter',
    text: 'The longest night. The year is told over, and one of its tales is kept from now on. Choose together.',
    reveal: 'tale',
    effect: { happiness: 2 },
  },
});

// THE YEAR CARD'S GOALS: small enough to finish in a sitting. `done` reads
// the town now against `mark`, a snapshot taken when the goal was chosen,
// so "found a village" means one more than you had. Met by Midwinter, the
// `reward` lands at the feast; missed, nothing is lost.
const G = (name, text, mark, done, reward, rewardText) => ({ name, text, mark, done, reward, rewardText });
export const YEAR_GOALS = {
  full_council: G('A Full Council', 'Every chair in the Hall taken on the longest night.', () => 0,
    (civ) => (civ.hall?.council || []).length >= 3, { happiness: 2 }, '+2 happiness'),
  new_village: G('A New Village', 'Found one more village than the town has now.', (civ) => (civ.villages || []).length,
    (civ, m) => (civ.villages || []).length > m, { goods: 3 }, '+3 goods'),
  a_wonder_stage: G('A Stone Laid', 'Raise one more stage of the great work than stands now.', (civ) => civ.monument?.stages || 0,
    (civ, m) => (civ.monument?.stages || 0) > m || (civ.wonders || []).length > (m === 0 ? 0 : -1) && (civ.monument?.stages || 0) === 0 && (civ.wonders || []).length > 0 && false, { coin: 3 }, '+3 coin'),
  a_technology: G('Something New', 'Learn one more technology than the Scholars know now.', (civ) => (civ.techs || []).length,
    (civ, m) => (civ.techs || []).length > m, { knowledge: 3 }, '+3 knowledge'),
  two_technologies: G('A Year of Learning', 'Learn two more technologies than the Scholars know now.', (civ) => (civ.techs || []).length,
    (civ, m) => (civ.techs || []).length >= m + 2, { knowledge: 5 }, '+5 knowledge'),
  clear_a_camp: G('Clear the Ridge', 'Clear one more camp than the Frontier has cleared.', (civ) => civ.cleared || 0,
    (civ, m) => (civ.cleared || 0) > m, { goods: 2, happiness: 1 }, '+2 goods, +1 happiness'),
  a_town: G('A Charter', 'Charter one more village into a town.', (civ) => (civ.villages || []).filter((v) => v.chartered).length,
    (civ, m) => (civ.villages || []).filter((v) => v.chartered).length > m, { coin: 2, happiness: 1 }, '+2 coin, +1 happiness'),
  a_friend: G('A Friend Abroad', 'One more of the peoples friendly than are now.', (civ) => (civ.rivals || []).filter((r) => r.stance === 'friendly').length,
    (civ, m) => (civ.rivals || []).filter((r) => r.stance === 'friendly').length > m, { goods: 2, coin: 2 }, '+2 goods, +2 coin'),
  a_full_larder: G('A Full Larder', 'Ten food in the granary on the longest night.', () => 0,
    (civ) => civ.food >= 10, { happiness: 2 }, '+2 happiness'),
  a_road: G('The Road Out', 'Three more hexes of road than there are now.', (civ) => (civ.roads || []).length,
    (civ, m) => (civ.roads || []).length >= m + 3, { coin: 3 }, '+3 coin'),
  a_law: G('Written Down', 'One more standing law than the Hall has now.', (civ) => (civ.hall?.laws || []).length,
    (civ, m) => (civ.hall?.laws || []).length > m, { knowledge: 2, happiness: 1 }, '+2 knowledge, +1 happiness'),
  a_family: G('Room for One More', 'One more person in the town than there are now.', (civ) => civ.population,
    (civ, m) => civ.population > m, { food: 3 }, '+3 food'),
};
export const YEAR_GOAL_ORDER = Object.keys(YEAR_GOALS);
// A goal that is already met the day it is offered is no goal. What can be offered now.
export const yearGoalsOpen = (civ) => YEAR_GOAL_ORDER.filter((id) => {
  const g = YEAR_GOALS[id];
  if (g.done(civ, g.mark(civ))) return false;
  if (id === 'a_wonder_stage' && !civ.monument?.revealed) return false;
  if (id === 'a_friend' && !(civ.rivals || []).some((r) => r.met && r.stance !== 'friendly')) return false;
  if (id === 'a_road' && !(civ.techs || []).includes('paths')) return false;
  if (id === 'a_law' && !(civ.branches || []).includes('statecraft')) return false;
  if (id === 'clear_a_camp' && !(civ.menaces || []).some((m) => m.kind === 'camp')) return false;
  if (id === 'a_town' && !(civ.villages || []).some((v) => !v.chartered)) return false;
  return true;
});

// THE WINTER TALES. At Midwinter two are offered and one is kept for good:
// a standing rule of THIS campaign, read through modsOf like a law or a
// wonder's grant. EVERY TALE IS A TRADE - a gift with a cost inside it -
// and the two offered are always the same weight, so the choice is which
// trade fits this town, never whether to be hurt (Matt, 2026-09-27: "we
// have to be careful that we don't offer them a cake or death situation").
// Anyone adding a tale keeps to that: one `gift`, one `cost`, both real.
const TALE = (name, text, gift, cost, mods, extra = {}) => ({ name, text, gift, cost, mods, ...extra });
export const TALES = {
  comet: TALE('The Year of the Comet', 'A comet hung over the town all winter.',
    'the Scholars read one property for certain each spring', 'happiness fades one faster',
    { springReveal: 1, fadeMore: 1 }),
  long_winter: TALE('The Long Winter', 'Snow lay until May. The town keeps a deeper granary now and plants later.',
    'the granary holds 3 more', 'the fields make 1 less each night',
    { larder: 3, fieldLess: 1 }),
  river_froze: TALE('The Year the River Froze', 'The river froze and could be walked across, by anyone.',
    'your units see one hex further', 'raiders walk one step further each night',
    { sight: 1, raiderStep: 1 }),
  plague: TALE('The Plague Year', 'Every house lost someone.',
    'the glow fades one slower for good', 'a person is lost now',
    { fadeLess: 1 }, { now: { population: -1 } }),
  bright_market: TALE('The Year of the Fair', 'Traders came from three days away.',
    'markets pay +1 coin', 'Hall cards cost 1 more to buy',
    { marketCoin: 1, buyMore: 1 }),
  old_songs: TALE('The Year of the Old Songs', 'The old songs were written down before the last singer died.',
    'every song or custom the Hall plays is +1 happiness', 'the Hall draws one card fewer at dawn',
    { hallJoy: 1, drawLess: 1 }),
  wolf_winter: TALE('The Wolf Winter', 'Wolves came into the lanes. The town keeps a watch now.',
    'raiders reaching the city meet one more garrison die', 'wolves come out of the dens twice as often',
    { garrisonDice: 1, wolvesMore: 1 }),
  wet_spring: TALE('The Wet Spring', 'The fields stood in water until June and grew thick after.',
    'fields make +1 each night', 'the granary holds 3 fewer',
    { fieldMore: 1, larder: -3 }),
  hard_frost: TALE('The Hard Frost', 'Frozen ground: easy walking, thin hunting.',
    'rough ground costs one step, not two', 'the hunt rolls one fewer die',
    { marchRange: 1, huntLess: 1 }),
  quiet_year: TALE('The Quiet Year', 'Nothing much happened.',
    'the town starts each year with +2 happiness', 'no family arrives in a year that starts under 7 happiness',
    { yearJoy: 2, birthsNeedJoy: 1 }),
};
export const TALE_ORDER = Object.keys(TALES);
export const talesOf = (civ) => civ?.tales || [];
export const taleMods = (civ) => talesOf(civ).reduce((acc, id) => {
  Object.entries(TALES[id]?.mods || {}).forEach(([k, v]) => { acc[k] = (acc[k] || 0) + v; });
  return acc;
}, {});
export const talesOpen = (civ) => TALE_ORDER.filter((id) => !talesOf(civ).includes(id));

/** Who a technology helps, in words: "the Frontier and the Stewards". */
export const techForText = (id, label) => {
  const r = TECHS[id]?.forRole;
  if (!r || r === 'any') return 'everyone';
  return (Array.isArray(r) ? r : [r]).map((x) => `the ${label(x)}`).join(' and ');
};
